import FormularioInscricao from './FormularioInscricao';
import ReCAPTCHA from 'react-google-recaptcha';
import { BuscaCEP } from '@/modules/structure/endereco/correios.service';
import { CandidatoService } from './candidato.service';
import { CreateCandidatoUiDto, buildValidationToastMessage, findAllErrors, validateRequiredFields, } from './inscricao.utils';
import { CursoService } from '@/modules/structure/curso/curso.service';
import { InstituicaoEnsinoService } from '@/modules/structure/instituicoes/instituicao.service';
import {
  CANDIDATO_TOKEN_KEY,
  LOGO_DEFENSORIA_SRC,
  hasCandidatoPublicCredential,
  inscricaoRascunhoStorageKey,
  saveCandidatoAccessToken,
} from './candidato.constants';
import { MunicipioService, MunicipioIBGE } from '@/modules/structure/municipio/municipio.service';
import { ProcessoSeletivoService, ReadProcessoSeletivoDto } from '../processo-seletivo/processo-seletivo.service';
import { ReadComarcaDto, ReadDistritoDto, ReadInstituicaoEnsinoDto } from '@/api.types';
import { abrirPdfCandidatoNovaAba, baixarArquivoCandidato, baixarProvaProcessoSeletivoPublica, } from './inscricao-files';
import { readComarcaPublic } from '@/modules/structure/comarca/comarca.service';
import { readDistritoPublic } from '@/modules/structure/distrito/distrito.service';
import { toast } from 'react-toastify';
import { useEffect, useState, useRef, MutableRefObject } from 'react';
import { useForm } from 'react-hook-form';
import { useInscricaoSubmit } from './useInscricaoSubmit';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const candidatoService = new CandidatoService();
const processoSeletivoService = new ProcessoSeletivoService();
const cursoService = new CursoService();
const instituicaoEnsinoService = new InstituicaoEnsinoService();
const municipioService = new MunicipioService();

export default function InscricaoCandidatoPage() {

  const { register, handleSubmit, setValue, watch, formState: { errors }, trigger } = useForm<CreateCandidatoUiDto>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldFocusError: false, // Desabilitar foco automático - usamos mensagens de toast para informar erros
    criteriaMode: 'all', // Validar todos os campos, não apenas o primeiro erro
  });

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const processoSeletivoId = params.id ? Number(params.id) : 0;
  const [candidatoId, setCandidatoId] = useState<number | null>(null);

  const telefoneNumeroRef = useRef<HTMLInputElement | null>(null) as MutableRefObject<HTMLInputElement | null>;
  const celularNumeroRef = useRef<HTMLInputElement | null>(null) as MutableRefObject<HTMLInputElement | null>;

  const [processoSeletivo, setProcessoSeletivo] = useState<ReadProcessoSeletivoDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCEP, setIsLoadingCEP] = useState(false);
  const [, setCursos] = useState<Array<{ id: number; nome: string }>>([]);
  const [comarcas, setComarcas] = useState<ReadComarcaDto[]>([]);
  const [distritos, setDistritos] = useState<ReadDistritoDto[]>([]);
  const [instituicoesEnsino, setInstituicoesEnsino] = useState<ReadInstituicaoEnsinoDto[]>([]);
  const [, setMunicipios] = useState<MunicipioIBGE[]>([]);
  const [municipiosDisponiveis, setMunicipiosDisponiveis] = useState<MunicipioIBGE[]>([]);
  const [municipioCandidato, setMunicipioCandidato] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [criandoRascunho, setCriandoRascunho] = useState(false);
  const [readOnly, setReadOnly] = useState(Boolean((location.state as any)?.readonly));
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const cursoPretendidoId = watch('cursoPretendidoId');
  const comarcaPretendidaId = watch('comarcaPretendidaId');
  const pessoaComDeficiencia = watch('pessoaComDeficiencia');
  const optaVagasPcd = watch('optaVagasPcd');
  const declaraIndigena = watch('declaraIndigena');
  const raca = watch('raca');
  const instituicaoEnsinoId = watch('instituicaoEnsinoId');
  const instituicaoEnsinoIdStr = String(instituicaoEnsinoId || '');

  useEffect(() => {
    const wantsReadonly = Boolean((location.state as any)?.readonly);
    if (wantsReadonly) {
      setReadOnly(true);
    }

    if (processoSeletivoId > 0) {
      const aplicarProcesso = (result: ReadProcessoSeletivoDto) => {
        setProcessoSeletivo(result);
        setValue('processoSeletivoId', processoSeletivoId);

        if (result.cursos && result.cursos.length > 0) {
          setCursos(result.cursos);
        }

        if (result.vagas && result.vagas.length > 0) {
          const municipiosUnicos = new Set<string>();
          result.vagas.forEach((vaga) => {
            if (vaga.municipio) {
              municipiosUnicos.add(vaga.municipio);
            }
          });

          municipioService
            .getMunicipiosSergipe()
            .then((todosMunicipios) => {
              setMunicipios(todosMunicipios);
              const municipiosFiltrados = todosMunicipios.filter((m) => municipiosUnicos.has(m.nome));
              setMunicipiosDisponiveis(municipiosFiltrados);
            })
            .catch((error) => {
              console.error('Falha ao carregar municípios:', error);
            });
        } else {
          setMunicipiosDisponiveis([]);
        }
      };

      processoSeletivoService
        .findOnePublic(processoSeletivoId)
        .then(aplicarProcesso)
        .catch(async () => {
          // Processo fechado: permitir visualizar inscrição própria (portal) sem redirecionar
          const fromStateId =
            (location.state as any)?.candidatoId != null
              ? Number((location.state as any).candidatoId)
              : null;
          const hasPortalToken = Boolean(localStorage.getItem(CANDIDATO_TOKEN_KEY));
          if (wantsReadonly || (hasPortalToken && fromStateId)) {
            try {
              const nomeInfo = await processoSeletivoService.findOnePublicNome(processoSeletivoId);
              setReadOnly(true);
              aplicarProcesso({
                id: nomeInfo.id,
                nome: nomeInfo.nome,
                dataInicioInscricoes: '',
                dataFimInscricoes: '',
                status: 'fechado',
                temTaxaInscricao: false,
                comProva: false,
                valorTaxa: null as any,
                dataVencimentoBoleto: null as any,
                editalUrl: null as any,
                cursos: [],
                vagas: [],
                editadoPor: { id: 0, username: '' },
                editadoEm: '',
              } as unknown as ReadProcessoSeletivoDto);
              return;
            } catch {
              /* cai no redirect abaixo */
            }
          }
          navigate(`/processo-seletivo/nao-disponivel/${processoSeletivoId}`);
        });
    }

    readComarcaPublic({ skip: 0, take: 9999 })
      .then((result) => {
        setComarcas(result[0]);
      })
      .catch((error) => {
        toast.error('Falha ao carregar comarcas: ' + error);
      });

    municipioService.getMunicipiosSergipe()
      .then((result) => {
        setMunicipios(result);
      })
      .catch((error) => {
        console.error('Falha ao carregar municípios:', error);
      });

    instituicaoEnsinoService.EnumeratePublic()
      .then((result) => {
        setInstituicoesEnsino(result);
      })
      .catch((error) => {
        console.error('Falha ao carregar instituições de ensino:', error);
      });
  }, [processoSeletivoId, navigate, setValue]);

  useEffect(() => {
    if (processoSeletivoId <= 0 || !processoSeletivo) {
      return;
    }
    const key = inscricaoRascunhoStorageKey(processoSeletivoId);

    const fromQueryRaw = new URLSearchParams(location.search).get('candidatoId');
    const fromQuery = fromQueryRaw != null ? Number(fromQueryRaw) : null;
    const fromState = (location.state as any)?.candidatoId != null ? Number((location.state as any).candidatoId) : null;
    const storedRaw = sessionStorage.getItem(key);
    const fromStorage = storedRaw != null ? Number(storedRaw) : null;

    const isValidId = (v: unknown): v is number =>
      typeof v === 'number' && Number.isFinite(v) && v > 0;

    /** Só reutiliza inscrição existente se houver token da inscrição ou JWT (portal/gestão). */
    const tryReuse = (id: number): boolean => {
      if (!hasCandidatoPublicCredential(id)) {
        return false;
      }
      sessionStorage.setItem(key, String(id));
      setCandidatoId(id);
      return true;
    };

    if (isValidId(fromQuery)) {
      if (tryReuse(fromQuery)) {
        navigate(`/candidato/inscricao/${processoSeletivoId}`, { replace: true, state: location.state });
        return;
      }
      sessionStorage.removeItem(key);
    } else if (isValidId(fromState)) {
      if (tryReuse(fromState)) {
        return;
      }
      sessionStorage.removeItem(key);
    } else if (isValidId(fromStorage)) {
      if (tryReuse(fromStorage)) {
        return;
      }
      sessionStorage.removeItem(key);
    }

    // Em modo Visualizar não cria rascunho novo
    if (readOnly || Boolean((location.state as any)?.readonly)) {
      if (isValidId(fromState) && !hasCandidatoPublicCredential(fromState)) {
        toast.error('Faça login no portal do candidato para visualizar esta inscrição.');
        navigate('/candidato/login');
      }
      return;
    }

    let cancelled = false;
    setCriandoRascunho(true);
    (async () => {
      try {
        const { candidatoId: id, accessToken } = await candidatoService.createPublicRascunho(processoSeletivoId);
        if (!cancelled) {
          sessionStorage.setItem(key, String(id));
          saveCandidatoAccessToken(id, accessToken);
          setCandidatoId(id);
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message || e?.message || 'Não foi possível iniciar a inscrição.');
      } finally {
        if (!cancelled) {
          setCriandoRascunho(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [processoSeletivoId, processoSeletivo, location.search, location.state, navigate, readOnly]);

  useEffect(() => {
    if (candidatoId && processoSeletivoId > 0) {
      candidatoService.findOnePublic(candidatoId)
        .then((candidato) => {

          const pessoa = candidato.pessoa;

          if (pessoa?.nome) setValue('nome', pessoa.nome);
          if (pessoa?.cpf) setValue('cpf', pessoa.cpf);

          if (pessoa?.dataNascimento) {
            const raw = pessoa.dataNascimento as string;
            if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
              setValue('dataNascimento', raw.slice(0, 10));
            } else {
              const data = new Date(raw);
              if (!isNaN(data.getTime())) {
                const year = data.getUTCFullYear();
                const month = String(data.getUTCMonth() + 1).padStart(2, '0');
                const day = String(data.getUTCDate()).padStart(2, '0');
                setValue('dataNascimento', `${year}-${month}-${day}`);
              }
            }
          }

          if (pessoa?.email) {
            setValue('email', pessoa.email);
          }

          if (pessoa?.telefone) {
            setValue('telefone', pessoa.telefone);
          }

          if (pessoa?.celular) {
            setValue('celular', pessoa.celular);
          }

          if (pessoa?.endereco) {
            setValue('endereco', pessoa.endereco);
          }

          if (candidato.instituicaoEnsinoId != null && candidato.instituicaoEnsinoId !== undefined) {
            setValue('instituicaoEnsinoId', candidato.instituicaoEnsinoId);
          } else if (candidato.instituicaoEnsinoNome) {
            setValue('instituicaoEnsinoId', 'outros' as any);

          }
          if (candidato.instituicaoEnsinoNome) setValue('instituicaoEnsinoNome', candidato.instituicaoEnsinoNome);
          if (candidato.nomeSocial) setValue('nomeSocial', candidato.nomeSocial);
          if (candidato.periodoAtualCurso) setValue('periodoAtualCurso', candidato.periodoAtualCurso);
          if (candidato.anoInicioCurso) setValue('anoInicioCurso', candidato.anoInicioCurso);
          if (candidato.cursoPretendidoId) setValue('cursoPretendidoId', candidato.cursoPretendidoId);
          if (candidato.comarcaPretendidaId) setValue('comarcaPretendidaId', candidato.comarcaPretendidaId);
          if (candidato.distritoPretendidoId) setValue('distritoPretendidoId', candidato.distritoPretendidoId);
          // Armazenar município para setar depois que os municípios forem carregados
          if (candidato.municipio) {
            setMunicipioCandidato(candidato.municipio);
          }
          if (candidato.genero) setValue('genero', candidato.genero as any);
          if (candidato.raca) setValue('raca', candidato.raca as any);
          if (candidato.optaVagasNegros !== undefined) setValue('optaVagasNegros', candidato.optaVagasNegros);
          if (candidato.declaraIndigena !== undefined) setValue('declaraIndigena', candidato.declaraIndigena);

          // Carregar pessoaComDeficiencia diretamente do backend (agora temos essa coluna)
          let pessoaComDeficienciaValue: boolean;

          if (candidato.pessoaComDeficiencia !== undefined) {
            pessoaComDeficienciaValue = candidato.pessoaComDeficiencia;
            setValue('pessoaComDeficiencia', pessoaComDeficienciaValue);
          } else {
            // Fallback: se não houver valor salvo, inferir baseado nos dados existentes
            // (para candidatos antigos que não têm esse campo preenchido)
            const temCamposDeficiencia = !!(candidato.descricaoDeficiencia && candidato.descricaoDeficiencia.trim() !== '') ||
              !!(candidato.laudoMedicoUrl && candidato.laudoMedicoUrl.trim() !== '') ||
              candidato.necessitaCuidadosEspeciais !== undefined;

            if (candidato.optaVagasPcd !== undefined && candidato.optaVagasPcd !== null) {
              pessoaComDeficienciaValue = true;
            } else if (temCamposDeficiencia) {
              pessoaComDeficienciaValue = true;
            } else {
              pessoaComDeficienciaValue = false;
            }
            setValue('pessoaComDeficiencia', pessoaComDeficienciaValue);
          }

          // Só carregar campos relacionados a deficiência se pessoaComDeficiencia for true
          if (pessoaComDeficienciaValue === true) {
            if (candidato.optaVagasPcd !== undefined) setValue('optaVagasPcd', candidato.optaVagasPcd);
            if (candidato.descricaoDeficiencia) setValue('descricaoDeficiencia', candidato.descricaoDeficiencia);
            if (candidato.necessitaCuidadosEspeciais !== undefined) setValue('necessitaCuidadosEspeciais', candidato.necessitaCuidadosEspeciais);
          } else {
            // Limpar campos relacionados quando pessoaComDeficiencia é false
            setValue('optaVagasPcd', undefined);
            setValue('descricaoDeficiencia', undefined);
            setValue('necessitaCuidadosEspeciais', undefined);
            setValue('laudoMedicoUrl', undefined);
          }
          if (candidato.declaracaoVeracidade !== undefined) setValue('declaracaoVeracidade', candidato.declaracaoVeracidade);
          if (candidato.confirmacaoEdital !== undefined) setValue('confirmacaoEdital', candidato.confirmacaoEdital);
          if (candidato.consentimentoLgpd !== undefined) setValue('consentimentoLgpd', candidato.consentimentoLgpd);

          // Carregar arquivos existentes (se houver)
          if (candidato.documentacaoIndigenaUrl) setValue('documentacaoIndigenaUrl', candidato.documentacaoIndigenaUrl);
          if (candidato.laudoMedicoUrl) setValue('laudoMedicoUrl', candidato.laudoMedicoUrl);
          if (candidato.documentoIdentificacaoUrl) setValue('documentoIdentificacaoUrl', candidato.documentoIdentificacaoUrl);
          if (candidato.comprovanteResidenciaUrl) setValue('comprovanteResidenciaUrl', candidato.comprovanteResidenciaUrl);
          if (candidato.documentoEstudoUrl) setValue('documentoEstudoUrl', candidato.documentoEstudoUrl);
          if (candidato.provaUrl) setValue('provaUrl', candidato.provaUrl);
          if (candidato.documentosComplementaresUrl) setValue('documentosComplementaresUrl', candidato.documentosComplementaresUrl);
        })
        .catch((error) => {
          console.error('Erro ao carregar dados do candidato:', error);
          toast.warn('Não foi possível carregar os dados da inscrição. Você pode preencher o formulário novamente.');
        });
    }
  }, [candidatoId, processoSeletivoId, setValue]);

  useEffect(() => {
    // Carregar cursos disponíveis quando curso pretendido mudar (endpoint público)
    if (cursoPretendidoId && processoSeletivo) {
      cursoService.findAllPublic({ skip: 0, take: 9999 })
        .then((result) => {
          setCursos(result[0]);
        })
        .catch((error) => {
          console.error('Falha ao carregar cursos:', error);
        });
    }

    // Limpar município selecionado quando o curso mudar (exceto se estiver carregando dados do candidato)
    if (cursoPretendidoId && !candidatoId) {
      setValue('municipio', '');
    }
  }, [cursoPretendidoId, processoSeletivo, setValue, candidatoId]);


  useEffect(() => {
    if (municipioCandidato && municipiosDisponiveis.length > 0 && cursoPretendidoId && processoSeletivo) {
      const municipioExisteNasVagas = processoSeletivo.vagas?.some(v =>
        v.cursoId === cursoPretendidoId && v.municipio === municipioCandidato
      );


      const municipioExisteNaLista = municipiosDisponiveis.some(m => m.nome === municipioCandidato);

      if (municipioExisteNasVagas || municipioExisteNaLista) {

        setTimeout(() => {
          setValue('municipio', municipioCandidato, { shouldValidate: true, shouldDirty: false });
        }, 200);
      }
    }
  }, [municipioCandidato, municipiosDisponiveis, cursoPretendidoId, processoSeletivo, setValue]);

  useEffect(() => {

    if (comarcaPretendidaId) {
      readDistritoPublic({ comarca_id: comarcaPretendidaId, skip: 0, take: 9999, comDefensoria: true } as any)
        .then((result) => {
          setDistritos(result[0]);
        })
        .catch((error) => {
          console.error('Falha ao carregar distritos:', error);
          setDistritos([]);
        });
    } else {
      setDistritos([]);
    }
  }, [comarcaPretendidaId]);

  // Limpar campos de arquivo quando as condições não forem atendidas
  // Removido: não limpar documentacaoIndigenaUrl baseado em declaraIndigena

  useEffect(() => {
    if (!optaVagasPcd) {
      setValue('laudoMedicoUrl', undefined);
      setValue('descricaoDeficiencia', undefined);
      setValue('necessitaCuidadosEspeciais', false);
    }
  }, [optaVagasPcd, setValue]);

  useEffect(() => {
    if (raca === 'branca') {
      setValue('optaVagasNegros', undefined);
      setValue('declaraIndigena', undefined);
      setValue('documentacaoIndigenaUrl', undefined);
    }
    // Limpar documentacaoIndigenaUrl quando a raça não for indígena
    if (raca && raca !== 'indigena') {
      setValue('declaraIndigena', undefined);
      setValue('documentacaoIndigenaUrl', undefined);
    }
  }, [raca, setValue]);

  const buscarCEP = async (cep: string) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      setIsLoadingCEP(true);
      try {
        const resultado = await BuscaCEP(cepLimpo);

        // Verificar se o resultado é válido e não tem erro
        if (resultado && resultado.erro !== true) {
          // Manter o CEP formatado (com hífen)
          const cepFormatado = cepLimpo.substring(0, 5) + '-' + cepLimpo.substring(5, 8);

          // Preencher os campos do endereço
          setValue('endereco.cep', cepFormatado, { shouldValidate: true, shouldDirty: true });

          // Preencher logradouro (rua/avenida)
          if (resultado.logradouro) {
            setValue('endereco.logradouro', resultado.logradouro, { shouldValidate: true, shouldDirty: true });
          }

          // Preencher bairro
          if (resultado.bairro) {
            setValue('endereco.bairro', resultado.bairro, { shouldValidate: true, shouldDirty: true });
          }

          // Preencher cidade (localidade)
          if (resultado.localidade) {
            setValue('endereco.cidade', resultado.localidade, { shouldValidate: true, shouldDirty: true });
          }

          // Preencher estado (UF)
          if (resultado.uf) {
            setValue('endereco.estado', resultado.uf, { shouldValidate: true, shouldDirty: true });
          }
        } else if (resultado && resultado.erro === true) {
          // CEP não encontrado na base de dados
          toast.warning('CEP não encontrado. Verifique o CEP digitado.');
          // Limpar campos quando CEP não encontrado
          setValue('endereco.logradouro', '', { shouldValidate: false });
          setValue('endereco.bairro', '', { shouldValidate: false });
          setValue('endereco.cidade', '', { shouldValidate: false });
          setValue('endereco.estado', '', { shouldValidate: false });
        } else {
          // Resultado undefined ou null
          toast.warning('CEP não encontrado. Verifique o CEP digitado.');
          // Limpar campos quando resultado é undefined
          setValue('endereco.logradouro', '', { shouldValidate: false });
          setValue('endereco.bairro', '', { shouldValidate: false });
          setValue('endereco.cidade', '', { shouldValidate: false });
          setValue('endereco.estado', '', { shouldValidate: false });
        }
      } catch (error) {
        toast.error('Erro ao buscar CEP. Tente novamente.');
        console.error('Erro ao buscar CEP:', error);
        // Limpar campos em caso de erro
        setValue('endereco.logradouro', '', { shouldValidate: false });
        setValue('endereco.bairro', '', { shouldValidate: false });
        setValue('endereco.cidade', '', { shouldValidate: false });
        setValue('endereco.estado', '', { shouldValidate: false });
      } finally {
        setIsLoadingCEP(false);
      }
    } else {
      setIsLoadingCEP(false);
    }
  };

  const handleDownloadFile = async (fileId: string, filename: string) => {
    try {
      await baixarArquivoCandidato(fileId, filename, candidatoId);
    } catch {
      toast.error('Erro ao baixar arquivo.');
    }
  };

  const handleDownloadProcessProva = async () => {
    try {
      if (!processoSeletivo?.id || !processoSeletivo?.provaUrl) return;
      await baixarProvaProcessoSeletivoPublica(processoSeletivo.id);
    } catch {
      toast.error('Erro ao baixar a prova do processo seletivo.');
    }
  };

  const handleViewFile = async (fileId: string) => {
    try {
      await abrirPdfCandidatoNovaAba(fileId, candidatoId);
    } catch {
      toast.error('Erro ao abrir arquivo.');
    }
  };

  /**
   * Função para processar e exibir erros
   * @param formErrors 
   * @param formData 
   */
  const processAndShowErrors = (formErrors: any, formData?: CreateCandidatoUiDto) => {
    const validationErrors = formData ? validateRequiredFields(formData) : [];

    const allErrors: string[] = [];

    const emailMismatchError = validationErrors.find(e => e.isEmailMismatch);
    const otherManualErrors = validationErrors.filter(e => !e.isEmailMismatch);

    otherManualErrors.forEach(err => {
      if (!allErrors.includes(err.field)) {
        allErrors.push(err.field);
      }
    });

    const temOutrosComNome = formData?.instituicaoEnsinoNome != null && String(formData.instituicaoEnsinoNome).trim() !== '';

    if (formErrors) {
      const reactHookFormErrors = findAllErrors(formErrors);
      reactHookFormErrors.forEach(err => {
        if (err === 'instituicaoEnsinoId' && temOutrosComNome) return;
        if (!allErrors.includes(err)) {
          allErrors.push(err);
        }
      });
    }
    if (temOutrosComNome && allErrors.includes('instituicaoEnsinoId')) {
      const idx = allErrors.indexOf('instituicaoEnsinoId');
      allErrors.splice(idx, 1);
    }

    if (allErrors.length === 0 && validationErrors.length > 0) {
      validationErrors.forEach(err => {
        if (!allErrors.includes(err.field)) {
          allErrors.push(err.field);
        }
      });
    }

    let errorMessage = buildValidationToastMessage(
      allErrors.length > 0 ? allErrors : validationErrors.map((e) => e.field),
      Boolean(emailMismatchError),
    );

    if (allErrors.length === 0 && validationErrors.length === 0 && !emailMismatchError) {
      errorMessage = 'Por favor, preencha todos os campos obrigatórios marcados com asterisco (*).';
    }

    toast.error(errorMessage, {
      autoClose: 10000,
      style: { whiteSpace: 'pre-line' },
      position: 'top-right',
    });

    setTimeout(() => { }, 100);
  };

  const onError = (formErrors: any) => {
    const currentData = watch();

    setTimeout(() => {
      processAndShowErrors(formErrors, currentData);
    }, 100);
  };

  const onSubmit = useInscricaoSubmit({
    candidatoService,
    candidatoId,
    processoSeletivoId,
    processoSeletivo,
    captchaToken,
    setCaptchaToken,
    recaptchaRef,
    setIsLoading,
    navigate,
    onFalhaValidacaoManual: (data) => processAndShowErrors({}, data),
  });

  if (!processoSeletivo || (criandoRascunho && candidatoId == null)) {
    return (
      <div className="row">
        <div className="col-12 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          {criandoRascunho && candidatoId == null ? (
            <p className="text-muted mt-3 small">Preparando sua inscrição…</p>
          ) : null}
        </div>
      </div>
    );
  }

  const municipiosFiltrados = cursoPretendidoId && processoSeletivo.vagas ? municipiosDisponiveis
    .filter(m => {
      return processoSeletivo.vagas?.some(v =>
        v.cursoId === cursoPretendidoId && v.municipio === m.nome
      );
    })
    : municipiosDisponiveis;

  const comarcasDisponiveis: number[] = processoSeletivo.vagas
    ?.filter(v => v.cursoId === cursoPretendidoId)
    .map(v => v.comarcaId)
    .filter((v): v is number => v != null)
    .filter((v, i, arr) => arr.indexOf(v) === i) ?? [];

  const distritosDisponiveis = processoSeletivo.vagas
    ?.filter(v => v.cursoId === cursoPretendidoId && v.comarcaId === comarcaPretendidaId)
    .map(v => v.distritoId)
    .filter((v): v is number => v !== undefined && v !== null)
    .filter((v, i, arr) => arr.indexOf(v) === i) || [];

  return (
    <div className="row">
      <div className="card-body text-center p-4 mb-2">
        <img style={{ width: '150px', height: '150px' }} src={LOGO_DEFENSORIA_SRC} alt="Logo Defensoria" className="img-fluid" />
      </div>
      <div className="col-md-12 text-center mb-2">
        <h3> {processoSeletivo.nome}</h3>
        <div className="text-muted">
          <div className="col-12 text-center mb-4">
            <h3>
              Defensoria Pública do Estado de Sergipe
            </h3>
          </div>
          {processoSeletivo.dataInicioInscricoes && processoSeletivo.dataFimInscricoes ? (
            <p className="mb-0">
              Período de inscrições:{' '}
              {new Date(processoSeletivo.dataInicioInscricoes).toLocaleDateString('pt-BR')} até{' '}
              {new Date(processoSeletivo.dataFimInscricoes).toLocaleDateString('pt-BR')}
            </p>
          ) : (
            <p className="mb-0">Processo seletivo encerrado para novas inscrições</p>
          )}
        </div>
        <hr className="pb-4" />
      </div>
      <div>
        <h3 className="text-center">Formulário para Processo Seletivo</h3>
        {readOnly ? (
          <p className="text-center text-muted">Visualização da inscrição (somente leitura)</p>
        ) : null}
      </div>

      <form onSubmit={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (readOnly) return;

        const currentData = watch();

        const validationErrors = validateRequiredFields(currentData);
        if (validationErrors.length > 0) {
          const emailMismatch = Boolean(validationErrors.find((e) => e.isEmailMismatch));
          const fields = validationErrors.filter((e) => !e.isEmailMismatch).map((e) => e.field);
          toast.error(buildValidationToastMessage(fields, emailMismatch), {
            autoClose: 10000,
            style: { whiteSpace: 'pre-line' },
          });
          await trigger(undefined, { shouldFocus: false });
          return;
        }

        const isValid = await trigger(undefined, { shouldFocus: false });

        if (!isValid) {
          const currentData2 = watch();
          const validationErrors2 = validateRequiredFields(currentData2);

          if (validationErrors2.length > 0) {
            toast.error(buildValidationToastMessage(validationErrors2.map((e) => e.field), false), {
              autoClose: 10000,
              style: { whiteSpace: 'pre-line' },
            });
            return;
          }
        }

        handleSubmit(onSubmit as (data: CreateCandidatoUiDto) => void, onError)(e);
      }}>
        <FormularioInscricao
          register={register}
          setValue={setValue}
          watch={watch}
          errors={errors}
          isLoadingCEP={isLoadingCEP}
          setIsLoadingCEP={setIsLoadingCEP}
          buscarCEP={buscarCEP}
          telefoneNumeroRef={telefoneNumeroRef}
          celularNumeroRef={celularNumeroRef}
          processoSeletivo={processoSeletivo!}
          instituicoesEnsino={instituicoesEnsino}
          comarcas={comarcas}
          distritos={distritos}
          cursoPretendidoId={cursoPretendidoId}
          comarcaPretendidaId={comarcaPretendidaId}
          pessoaComDeficiencia={pessoaComDeficiencia}
          optaVagasPcd={optaVagasPcd}
          declaraIndigena={declaraIndigena}
          raca={raca}
          instituicaoEnsinoIdStr={instituicaoEnsinoIdStr}
          comarcasDisponiveis={comarcasDisponiveis}
          distritosDisponiveis={distritosDisponiveis}
          municipios={municipiosFiltrados}
          onFileValidationError={(message) => toast.error(message)}
          handleViewFile={handleViewFile}
          handleDownloadFile={handleDownloadFile}
          handleDownloadProcessProva={handleDownloadProcessProva}
          readOnly={readOnly}
        />
        {!readOnly ? (
          <div className="mb-4">
            <div className="mb-3">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'} // Chave de teste do Google (sempre mostra desafio de imagens)
                size="normal"
                theme="light"
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => {
                  setCaptchaToken(null);
                  toast.warning('O captcha expirou. Por favor, complete a verificação novamente.');
                }}
                onError={() => {
                  setCaptchaToken(null);
                  toast.error('Erro ao carregar o captcha. Por favor, recarregue a página.');
                }}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={isLoading || !captchaToken}
            >
              {isLoading ? 'Enviando...' : 'Enviar Inscrição'}
            </button>
          </div>
        ) : (
          <div className="mb-4">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate('/candidato/meus-processos')}
            >
              Voltar
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

